import React, { useMemo } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';

function CardioDataFilters({
  filters,
  onFilterChange,
  exercises
}) {
  // Filter to only cardio exercises (cardio_basic or cardio_segments)
  const cardioExercises = useMemo(() => {
    return exercises.filter(ex =>
      ex.exercise_type === 'cardio_basic' || ex.exercise_type === 'cardio_segments'
    );
  }, [exercises]);

  // Filter exercises based on selected cardio type
  const filteredExercises = useMemo(() => {
    if (!filters.cardioType || filters.cardioType === 'all') {
      return cardioExercises;
    }
    return cardioExercises.filter(ex => ex.exercise_type === filters.cardioType);
  }, [cardioExercises, filters.cardioType]);

  // Format exercises for react-select
  const exerciseOptions = useMemo(() => {
    return filteredExercises.map(ex => ({
      value: ex.id,
      label: `${ex.name} (${ex.exercise_type === 'cardio_basic' ? 'Basic Cardio' : 'Interval Workout'})`,
      exerciseData: ex
    }));
  }, [filteredExercises]);

  // Find selected exercise object for react-select
  const selectedExerciseOption = useMemo(() => {
    if (!filters.exerciseId) return null;
    return exerciseOptions.find(opt => opt.value === filters.exerciseId) || null;
  }, [filters.exerciseId, exerciseOptions]);

  const handleCardioTypeChange = (e) => {
    const newCardioType = e.target.value;
    // Clear exercise filter if it's not compatible with new cardio type
    if (filters.exerciseId && newCardioType !== 'all') {
      const selectedExercise = exercises.find(ex => ex.id === filters.exerciseId);
      if (selectedExercise && selectedExercise.exercise_type !== newCardioType) {
        onFilterChange({ ...filters, cardioType: newCardioType, exerciseId: null });
        return;
      }
    }
    onFilterChange({ ...filters, cardioType: newCardioType });
  };

  const handleExerciseChange = (option) => {
    onFilterChange({
      ...filters,
      exerciseId: option ? option.value : null
    });
  };

  const handleDateRangeChange = (e) => {
    const rangeValue = e.target.value;
    let startDate = null;
    let endDate = null;

    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    if (rangeValue === 'last_30') {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      startDate = formatDate(thirtyDaysAgo);
      endDate = formatDate(today);
    } else if (rangeValue === 'last_90') {
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(today.getDate() - 90);
      startDate = formatDate(ninetyDaysAgo);
      endDate = formatDate(today);
    } else if (rangeValue === 'all') {
      startDate = null;
      endDate = null;
    }

    onFilterChange({
      ...filters,
      dateRange: rangeValue,
      startDate,
      endDate
    });
  };

  const handleCustomDateChange = (field, value) => {
    onFilterChange({
      ...filters,
      dateRange: 'custom',
      [field]: value
    });
  };

  const clearFilters = () => {
    onFilterChange({
      cardioType: 'all',
      exerciseId: null,
      dateRange: 'all',
      startDate: null,
      endDate: null
    });
  };

  const hasActiveFilters =
    filters.cardioType !== 'all' ||
    filters.exerciseId ||
    filters.dateRange !== 'all';

  return (
    <div className="cardio-data-filters mb-4">
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Cardio Type</Form.Label>
            <Form.Select
              value={filters.cardioType || 'all'}
              onChange={handleCardioTypeChange}
            >
              <option value="all">All Cardio</option>
              <option value="cardio_basic">Basic Cardio</option>
              <option value="cardio_segments">Interval Workouts</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Exercise</Form.Label>
            <Select
              value={selectedExerciseOption}
              onChange={handleExerciseChange}
              options={exerciseOptions}
              isClearable
              placeholder="All exercises..."
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px'
                })
              }}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <Form.Select
              value={filters.dateRange || 'all'}
              onChange={handleDateRangeChange}
            >
              <option value="all">All Time</option>
              <option value="last_30">Last 30 Days</option>
              <option value="last_90">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {filters.dateRange === 'custom' && (
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {hasActiveFilters && (
        <Row>
          <Col>
            <Button
              variant="link"
              size="sm"
              onClick={clearFilters}
              className="p-0"
            >
              <i className="fas fa-times me-1"></i>
              Clear all filters
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default CardioDataFilters;
